import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import DocumentUpload from './DocumentUpload';
import EditPermitModal from './EditPermitModal';
import { permitService } from '../../../services/permitService';
import { calendarService } from '../../../services/calendarService';

const PermitDetails = ({ permit, onClose, onUpdate, canEdit = true, canDelete = true }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [permitData, setPermitData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    date: '',
    time: '09:00',
    notes: ''
  });
  const [scheduling, setScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSuccess, setScheduleSuccess] = useState('');
  const [inspections, setInspections] = useState([]);

  const displayPermit = permitData || permit;

  useEffect(() => {
    if (permit?.id) {
      loadPermitWithProject();
      loadInspections();
    }
  }, [permit?.id]);

  const loadPermitWithProject = async () => {
    setLoading(true);
    try {
      const data = await permitService?.getByIdWithProject(permit?.id);
      setPermitData(data || permit);
    } catch (error) {
      console.error('Error loading permit with project:', error);
      setPermitData(permit);
    } finally {
      setLoading(false);
    }
  };

  const loadInspections = async () => {
    try {
      const allEvents = await calendarService?.getAll();
      const permitInspections = allEvents?.filter(
        e => e?.permitId === permit?.id && e?.eventType === 'inspection'
      );
      setInspections(permitInspections || []);
    } catch (err) {
      console.error('Error loading inspections:', err);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    loadPermitWithProject();
    onUpdate?.();
  };

  const handleStatusUpdate = async (newStatus) => {
    setStatusError('');
    setStatusUpdating(true);
    try {
      await permitService?.updateStatus(permit?.id, newStatus);
      await loadPermitWithProject();
      onUpdate?.();
    } catch (err) {
      setStatusError(err?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleScheduleInspection = async (e) => {
    e?.preventDefault();
    if (!scheduleForm?.title?.trim() || !scheduleForm?.date) {
      setScheduleError('Title and date are required');
      return;
    }
    setScheduleError('');
    setScheduleSuccess('');
    setScheduling(true);
    try {
      const startDateTime = new Date(`${scheduleForm?.date}T${scheduleForm?.time}:00`);
      const endDateTime = new Date(startDateTime?.getTime() + 60 * 60 * 1000);

      await calendarService?.create({
        title: scheduleForm?.title,
        description: scheduleForm?.notes || `Inspection for permit ${displayPermit?.applicationNumber}`,
        eventType: 'inspection',
        startDate: startDateTime?.toISOString(),
        endDate: endDateTime?.toISOString(),
        allDay: false,
        permitId: permit?.id,
        projectId: displayPermit?.projectId || null,
        notes: scheduleForm?.notes || null
      });

      setScheduleSuccess('Inspection scheduled successfully!');
      setScheduleForm({ title: '', date: '', time: '09:00', notes: '' });
      setShowScheduleForm(false);
      await loadInspections();
    } catch (err) {
      setScheduleError(err?.message || 'Failed to schedule inspection');
    } finally {
      setScheduling(false);
    }
  };

  const handleNavigateToProject = () => {
    if (displayPermit?.projectId) {
      const event = new CustomEvent('openProjectDetails', {
        detail: { projectId: displayPermit?.projectId }
      });
      window.dispatchEvent(event);
      onClose?.();
    }
  };

  const handleContactInspector = () => {
    if (displayPermit?.inspectorEmail) {
      window.location.href = `mailto:${displayPermit?.inspectorEmail}?subject=Re: Permit ${displayPermit?.applicationNumber}`;
    } else if (displayPermit?.inspectorPhone) {
      window.location.href = `tel:${displayPermit?.inspectorPhone}`;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'submission': 'bg-blue-100 text-blue-700 border-blue-200',
      'under review': 'bg-warning/10 text-warning border-warning/20',
      'approved': 'bg-success/10 text-success border-success/20',
      'rejected': 'bg-destructive/10 text-destructive border-destructive/20'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'submission': 'Clock',
      'under review': 'Search',
      'approved': 'CheckCircle',
      'rejected': 'XCircle'
    };
    return icons?.[status] || 'FileText';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getDaysUntilDeadline = () => {
    if (!displayPermit?.deadline) return null;
    const today = new Date();
    const deadline = new Date(displayPermit?.deadline);
    return Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  };

  const daysUntil = getDaysUntilDeadline();

  const statusOptions = [
    { value: 'submission', label: 'Submission' },
    { value: 'under review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden sticky top-6">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 pr-2">
            <h3 className="font-semibold text-lg text-foreground mb-1 truncate">
              {displayPermit?.projectName}
            </h3>
            <p className="text-sm text-muted-foreground">{displayPermit?.applicationNumber || 'No application number'}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canEdit && (
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                title="Edit permit"
              >
                <Icon name="Edit" size={18} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border",
            getStatusColor(displayPermit?.status)
          )}>
            <Icon name={getStatusIcon(displayPermit?.status)} size={14} />
            {displayPermit?.status?.charAt(0)?.toUpperCase() + displayPermit?.status?.slice(1)}
          </span>
          {daysUntil !== null && (
            <span className={cn(
              "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
              daysUntil < 0 ? 'bg-destructive/10 text-destructive' :
              daysUntil <= 7 ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
            )}>
              <Icon name="Clock" size={12} />
              {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d left`}
            </span>
          )}
        </div>

        {statusError && (
          <p className="text-xs text-destructive mt-2">{statusError}</p>
        )}
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {['overview', 'inspections', 'documents']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              "flex-1 px-3 py-3 text-xs font-medium transition-colors capitalize",
              activeTab === tab
                ? "text-primary border-b-2 border-primary bg-primary/5" :"text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            {tab}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="p-4 md:p-5 max-h-[calc(100vh-320px)] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={24} className="animate-spin text-primary" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-5">
                {/* Linked Project */}
                {displayPermit?.projects && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="Briefcase" size={15} className="text-primary" />
                      Linked Project
                    </h4>
                    <button
                      onClick={handleNavigateToProject}
                      className="w-full text-left hover:bg-primary/10 rounded-lg p-2 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground text-sm">
                            {displayPermit?.projects?.customerName || displayPermit?.projects?.customers?.name || 'Unknown Customer'}
                          </p>
                          <p className="text-xs text-muted-foreground">{displayPermit?.projects?.address}</p>
                          <span className="inline-flex items-center gap-1 text-xs text-primary mt-1">
                            <Icon name="ExternalLink" size={11} />
                            View project
                          </span>
                        </div>
                        <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                      </div>
                    </button>
                  </div>
                )}

                {/* Permit Info */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Permit Information</h4>
                  <div className="space-y-2.5">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Type:</span>
                      <span className="text-sm font-medium text-foreground">{displayPermit?.permitType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Application #:</span>
                      <span className="text-sm font-medium text-foreground">{displayPermit?.applicationNumber || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Submitted:</span>
                      <span className="text-sm font-medium text-foreground">{formatDate(displayPermit?.submittedDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Deadline:</span>
                      <span className={cn(
                        "text-sm font-medium",
                        daysUntil !== null && daysUntil < 0 ? 'text-destructive' :
                        daysUntil !== null && daysUntil <= 7 ? 'text-warning' : 'text-foreground'
                      )}>
                        {formatDate(displayPermit?.deadline)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Update Status */}
                {canEdit && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Update Status</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {statusOptions?.map(opt => (
                      <button
                        key={opt?.value}
                        onClick={() => handleStatusUpdate(opt?.value)}
                        disabled={statusUpdating || displayPermit?.status === opt?.value}
                        className={cn(
                          "px-3 py-2 rounded-lg text-xs font-medium border transition-all",
                          displayPermit?.status === opt?.value
                            ? getStatusColor(opt?.value) + 'cursor-default' :'border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5',
                          statusUpdating && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {statusUpdating && displayPermit?.status !== opt?.value ? (
                          <span className="flex items-center justify-center gap-1">
                            <Icon name="Loader2" size={12} className="animate-spin" />
                            {opt?.label}
                          </span>
                        ) : opt?.label}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                {/* Inspector */}
                {(displayPermit?.inspectorName || displayPermit?.inspectorPhone || displayPermit?.inspectorEmail) && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-3">Assigned Inspector</h4>
                    <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                      {displayPermit?.inspectorName && (
                        <div className="flex items-center gap-2">
                          <Icon name="User" size={15} className="text-primary" />
                          <span className="text-sm font-medium text-foreground">{displayPermit?.inspectorName}</span>
                        </div>
                      )}
                      {displayPermit?.inspectorPhone && (
                        <div className="flex items-center gap-2">
                          <Icon name="Phone" size={15} className="text-muted-foreground" />
                          <a href={`tel:${displayPermit?.inspectorPhone}`} className="text-sm text-primary hover:underline">
                            {displayPermit?.inspectorPhone}
                          </a>
                        </div>
                      )}
                      {displayPermit?.inspectorEmail && (
                        <div className="flex items-center gap-2">
                          <Icon name="Mail" size={15} className="text-muted-foreground" />
                          <a href={`mailto:${displayPermit?.inspectorEmail}`} className="text-sm text-primary hover:underline truncate">
                            {displayPermit?.inspectorEmail}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {displayPermit?.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Notes</h4>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">{displayPermit?.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'inspections' && (
              <div className="space-y-4">
                {scheduleSuccess && (
                  <div className="bg-success/10 border border-success/20 text-success px-3 py-2 rounded text-sm">
                    {scheduleSuccess}
                  </div>
                )}

                {/* Schedule New Inspection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-foreground">Schedule Inspection</h4>
                    <button
                      onClick={() => { setShowScheduleForm(!showScheduleForm); setScheduleError(''); }}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Icon name={showScheduleForm ? 'ChevronUp' : 'Plus'} size={14} />
                      {showScheduleForm ? 'Cancel' : 'New'}
                    </button>
                  </div>

                  {showScheduleForm && (
                    <form onSubmit={handleScheduleInspection} className="bg-muted/30 rounded-lg p-4 space-y-3 border border-border">
                      {scheduleError && (
                        <p className="text-xs text-destructive">{scheduleError}</p>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Title *</label>
                        <input
                          type="text"
                          value={scheduleForm?.title}
                          onChange={e => setScheduleForm(prev => ({ ...prev, title: e?.target?.value }))}
                          placeholder="e.g. Foundation Inspection"
                          className="flex h-9 w-full rounded-md border border-input bg-white text-black px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">Date *</label>
                          <input
                            type="date"
                            value={scheduleForm?.date}
                            onChange={e => setScheduleForm(prev => ({ ...prev, date: e?.target?.value }))}
                            className="flex h-9 w-full rounded-md border border-input bg-white text-black px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">Time</label>
                          <input
                            type="time"
                            value={scheduleForm?.time}
                            onChange={e => setScheduleForm(prev => ({ ...prev, time: e?.target?.value }))}
                            className="flex h-9 w-full rounded-md border border-input bg-white text-black px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1">Notes</label>
                        <textarea
                          value={scheduleForm?.notes}
                          onChange={e => setScheduleForm(prev => ({ ...prev, notes: e?.target?.value }))}
                          rows={2}
                          placeholder="Additional notes..."
                          className="flex w-full rounded-md border border-input bg-white text-black px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        />
                      </div>
                      <Button type="submit" size="sm" className="w-full" disabled={scheduling}>
                        {scheduling ? (
                          <span className="flex items-center gap-2">
                            <Icon name="Loader2" size={14} className="animate-spin" />
                            Scheduling...
                          </span>
                        ) : 'Schedule Inspection'}
                      </Button>
                    </form>
                  )}
                </div>

                {/* Inspection List */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Scheduled Inspections ({inspections?.length})
                  </h4>
                  {inspections?.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                      <Icon name="Calendar" size={32} className="mx-auto mb-2 opacity-40" />
                      No inspections scheduled yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {inspections?.map(inspection => (
                        <div key={inspection?.id} className="bg-muted/50 rounded-lg p-3 border border-border">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-foreground">{inspection?.title}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDateTime(inspection?.startDate)}
                              </p>
                              {inspection?.notes && (
                                <p className="text-xs text-muted-foreground mt-1">{inspection?.notes}</p>
                              )}
                            </div>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Inspection
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <DocumentUpload permitId={permit?.id} />
            )}
          </>
        )}
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            iconName="Mail"
            onClick={handleContactInspector}
            disabled={!displayPermit?.inspectorEmail && !displayPermit?.inspectorPhone}
          >
            Contact Inspector
          </Button>
          {canEdit && (
            <Button
              className="flex-1"
              iconName="Edit"
              onClick={() => setShowEditModal(true)}
            >
              Edit Permit
            </Button>
          )}
        </div>
      </div>
      {showEditModal && (
        <EditPermitModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          permit={displayPermit}
        />
      )}
    </div>
  );
};

export default PermitDetails;